/**
 * Authentication Context for THE ONESCENT DIAL
 * Native email/password authentication with natal profile support.
 * 
 * REFACTORED: Now uses centralized AuthService and StorageService
 * for consistent API communication and state management.
 * 
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from '../components/ui/sonner';
import authService, { AUTH_EVENTS } from '../services/AuthService';
import storageService from '../services/StorageService';
import { authLogger } from '../utils/logger';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth on mount using the service
  useEffect(() => {
    const initAuth = async () => {
      try {
        const userData = await authService.initialize();
        setUser(userData);
      } catch (err) {
        authLogger.error('Auth initialization error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
    
    // Subscribe to auth state changes from the service
    const unsubscribe = authService.subscribe(({ user: newUser, loading: isLoading }) => {
      setUser(newUser);
      if (!isLoading) {
        setLoading(false);
      }
    });
    
    return unsubscribe;
  }, []);

  // Listen for auth events
  useEffect(() => {
    const handleLogin = (e) => {
      authLogger.log('Auth event: LOGIN', e.detail);
      // Sync local data to server after login
      storageService.syncLocalToServer();
    };
    
    const handleLogout = () => {
      authLogger.log('Auth event: LOGOUT');
    };
    
    const handleTokenExpired = () => {
      authLogger.warn('Auth event: TOKEN_EXPIRED');
      toast.error('Session expired', {
        description: 'Please sign in again.'
      });
    };
    
    window.addEventListener(AUTH_EVENTS.LOGIN, handleLogin);
    window.addEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
    window.addEventListener(AUTH_EVENTS.TOKEN_EXPIRED, handleTokenExpired);
    
    return () => {
      window.removeEventListener(AUTH_EVENTS.LOGIN, handleLogin);
      window.removeEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
      window.removeEventListener(AUTH_EVENTS.TOKEN_EXPIRED, handleTokenExpired);
    };
  }, []);

  /**
   * Register with email/password and natal profile data.
   */
  const register = async ({ 
    email, 
    password, 
    name, 
    natalData, 
    communicationConsent, 
    phone 
  }) => {
    setError(null);
    try {
      const userData = await authService.register({
        email,
        password,
        name,
        natalData,
        communicationConsent,
        phone,
      });
      
      setUser(userData);
      toast.success('Welcome to The Onescent Dial!', {
        description: 'Your account and natal profile have been created.'
      });
      
      // Dispatch legacy event for backward compatibility
      window.dispatchEvent(new CustomEvent('tod_auth_change', { detail: { authenticated: true } }));
      
      return userData;
    } catch (err) {
      setError(err.message);
      toast.error('Registration failed', {
        description: err.message
      });
      throw err;
    }
  };

  /**
   * Login with email/password
   */
  const login = async (email, password, keepLoggedIn = true) => {
    setError(null);
    try {
      const userData = await authService.login(email, password, keepLoggedIn);
      
      setUser(userData);
      toast.success(`Welcome back, ${userData.name?.split(' ')[0] || 'traveler'}!`);
      
      // Dispatch legacy event for backward compatibility
      window.dispatchEvent(new CustomEvent('tod_auth_change', { detail: { authenticated: true } }));
      
      return userData;
    } catch (err) {
      setError(err.message);
      toast.error('Login failed', {
        description: err.message
      });
      throw err;
    }
  };

  /**
   * Logout
   */
  const logout = async () => {
    await authService.logout();
    setUser(null);
    
    // Dispatch legacy event for backward compatibility
    window.dispatchEvent(new CustomEvent('tod_auth_change', { detail: { authenticated: false } }));
    
    toast.success('Signed out successfully');
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async () => {
    const userData = await authService.refreshUser();
    if (userData) {
      setUser(userData);
      
      // Update localStorage pro_code if it changed
      if (userData.pro_code) {
        localStorage.setItem('tod_pro_code', userData.pro_code);
      }
    }
    return userData;
  };

  /**
   * Apply pro code
   */
  const applyProCode = async (code) => {
    setError(null);
    try {
      const result = await authService.applyProCode(code);
      
      setUser(prev => ({ ...prev, pro_code: code, max_profiles: result.max_profiles }));
      toast.success('Pro code applied!', {
        description: `You now have ${result.max_profiles} profile slots.`
      });
      return result;
    } catch (err) {
      setError(err.message);
      toast.error('Invalid code', {
        description: err.message
      });
      throw err;
    }
  };

  /**
   * Get profiles (credit-based, NOT including natal)
   * Now uses StorageService
   */
  const getProfiles = useCallback(async () => {
    return storageService.getProfiles();
  }, []);

  /**
   * Save profile (uses credits)
   * Now uses StorageService
   */
  const saveProfile = async (profileData) => {
    try {
      const result = await storageService.saveProfile(profileData);
      
      if (user) {
        toast.success('Profile saved', {
          description: `"${profileData.name}" has been saved to your account.`
        });
      } else {
        toast.success('Profile saved locally', {
          description: 'Sign in to sync across devices'
        });
      }
      
      return result;
    } catch (err) {
      toast.error('Failed to save profile', {
        description: err.message
      });
      throw err;
    }
  };

  /**
   * Delete profile (frees credit)
   * Now uses StorageService
   */
  const deleteProfile = async (profileId, profileName) => {
    try {
      await storageService.deleteProfile(profileId);
      toast.success('Profile deleted', {
        description: profileName ? `"${profileName}" has been removed.` : undefined
      });
    } catch (err) {
      toast.error('Failed to delete profile');
      throw err;
    }
  };

  /**
   * Get the user's natal profile (separate from credit-based profiles).
   */
  const getNatalProfile = useCallback(() => {
    if (!user?.natal_profile) return null;
    
    return {
      name: user.name + "'s Natal",
      birth_date: user.natal_profile.birth_date,
      birth_time: user.natal_profile.birth_time,
      birth_location: user.natal_profile.birth_location,
      is_natal: true
    };
  }, [user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    refreshUser,
    applyProCode,
    getProfiles,
    saveProfile,
    deleteProfile,
    getNatalProfile,
    natalProfile: user?.natal_profile || null,
    maxProfiles: user?.max_profiles || 3,
    // New: expose remembered email
    rememberedEmail: authService.rememberedEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
